import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { InstallAppButton } from '@/components/customer/InstallAppButton';
import { loadRemembered, saveRemembered } from '@/hooks/useRememberedLogin';
import { Eye, EyeOff } from 'lucide-react';

const REMEMBER_KEY = 'tiffin_remembered_customer_login';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const saved = loadRemembered(REMEMBER_KEY);
    if (saved) {
      setEmail(saved.email);
      setPassword(saved.password);
      setRemember(true);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
      saveRemembered(REMEMBER_KEY, email, password, remember);
      navigate('/account');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white relative overflow-hidden flex flex-col items-center justify-center px-6 py-10">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1585937421612-70a008356fbe?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/80 to-obsidian" />

      <div className="relative z-10 w-full max-w-sm">
        <div className="text-center mb-8">
          <img
            src="/logo-emblem.png"
            alt="The Tiffin by The Virsa"
            data-testid="brand-logo"
            className="mx-auto w-28 h-28 rounded-full object-cover shadow-2xl ring-1 ring-white/10"
          />
          <p className="text-white/70 text-sm mt-4">Daily Fresh. Daily Different.</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 shadow-xl p-6 space-y-4 text-charcoal">
          <h2 className="font-heading text-xl font-semibold text-charcoal">Welcome back</h2>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pr-10"
              />
              <button
                type="button"
                data-testid="login-toggle-password-visibility"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-ink hover:text-charcoal transition-colors duration-150"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-muted-ink cursor-pointer select-none">
            <Checkbox
              data-testid="login-remember-me-checkbox"
              checked={remember}
              onCheckedChange={(v) => setRemember(!!v)}
            />
            Remember Me
          </label>

          {error && (
            <p data-testid="login-error" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover transition-colors duration-150"
          >
            {busy ? 'Signing in…' : 'Log In'}
          </Button>

          <Link to="/register" className="block">
            <Button
              type="button"
              data-testid="login-register-btn"
              className="w-full h-11 rounded-full bg-coriander hover:bg-coriander/90 text-white transition-colors duration-150"
            >
              Create an Account
            </Button>
          </Link>

          <p className="text-center text-sm pt-1">
            <Link to="/reset-with-code" data-testid="login-forgot-link" className="text-coriander hover:underline">
              Forgot password?
            </Link>
          </p>
        </form>
      </div>

      <div className="relative z-10">
        <InstallAppButton className="mt-8" />
      </div>
    </div>
  );
}
